<template>
  <div class="editable-field">
    <div v-if="isEditing" class="edit-mode">
      <BbTextarea
        v-if="multiline"
        variant="outlined"
        v-model="localValue"
        :rows="3"
        :auto-grow="true"
        :placeholder="t('common.title')"
      />
      <BbInput v-else v-bind="{ placeholder: t('common.title') }" v-model="localValue" />
      <BbBtn
        v-if="localValue !== ''"
        size="small"
        icon="checkmark-small"
        icon-type="outlined"
        is-icon-button
        without-background
        @click="confirmUpdate"
      />
      <BbBtn size="small" icon="x-small" icon-type="outlined" is-icon-button without-background @click="cancelEdit" />
    </div>
    <div v-else class="view-mode">
      <div class="view-mode__value" :class="{ 'view-mode__value_ellipsis': ellipsis }">{{ value }}</div>
      <BbBtn
        v-if="displayEditingIcon"
        size="small"
        icon="edit"
        icon-type="outlined"
        is-icon-button
        without-background
        @click="$emit('set-editable')"
      />
    </div>
  </div>
</template>

<script>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

export default {
  emits: ['update', 'cancel', 'set-editable'],
  name: 'EditableField',
  props: {
    value: {
      type: String,
      required: true,
    },
    isEditing: {
      type: Boolean,
      required: true,
    },
    placeholder: {
      type: String,
      default: 'Enter value',
    },
    displayEditingIcon: {
      type: Boolean,
      default: false,
    },
    ellipsis: {
      type: Boolean,
      default: true,
    },
    multiline: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();

    const localValue = ref(props.value);

    watch(
      () => props.value,
      (newValue) => {
        localValue.value = newValue;
      },
    );

    const confirmUpdate = () => {
      emit('update', localValue.value);
    };

    const cancelEdit = () => {
      localValue.value = props.value; // Reset to original value
      emit('cancel');
    };

    return {
      localValue,
      confirmUpdate,
      cancelEdit,
      t,
    };
  },
};
</script>

<style scoped lang="scss">
.editable-field {
  display: flex;
  align-items: center;
  gap: $bb-spacing-1;
  width: 100%;
  height: 20px;

  .bb-input {
    width: 100% !important;
  }

  .bb-btn.primary {
    width: 32px;
    height: 32px;
    border-radius: 12px;
    background: $bb-neutral-6 !important;
    color: #1c1c1d !important;

    &:hover {
      background: $bb-neutral-6 !important;
    }

    &:nth-of-type(2) {
      background: $bb-interface-5 !important;
      color: $bb-neutral-0 !important;
      margin-left: $bb-spacing-1;
    }
  }
}

.edit-mode {
  display: flex;
  align-items: center;
  gap: $bb-spacing-1;
  width: 100%;
}

.view-mode {
  display: flex;
  gap: $bb-spacing-1;
  max-width: 100%;
  flex-shrink: 1;
  overflow: hidden;
  align-items: center;
  display: flex;

  &__value {
    @include bb-regular-text-styles('md');

    @media (min-width: 1440px) {
      @include bb-regular-text-styles('default');
    }

    width: 100%;
    max-width: 100%;
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.2px;

    &_ellipsis {
      overflow: hidden;
      word-break: break-all;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
  }
}
</style>
